/**
 * ONE STREAM PER BROWSER, not per tab — and the handover when that tab goes away.
 *
 * WHY. The server caps a person at five concurrent streams and expels the oldest when a sixth
 * opens (§13 `overflow`). Six tabs of the same application would spend that budget on themselves
 * and start evicting each other, and every open tab costs the server a socket and a Redis
 * subscription for frames the other tabs are already receiving. So the tabs elect ONE leader; the
 * leader holds the `EventSource` and relays every frame to the others over a `BroadcastChannel`.
 *
 * ELECTION. `navigator.locks` when the browser has it: the lock is held for as long as the tab
 * lives and is released BY THE BROWSER when it dies — including a crash or a killed process,
 * which no heartbeat scheme detects reliably. Whoever is waiting on the lock becomes the leader
 * within milliseconds. Where Web Locks is missing (older Safari), a lease over the same
 * `BroadcastChannel`: the leader announces every `LEASE_INTERVAL_MS`, and a follower that has not
 * heard one for `LEASE_TIMEOUT_MS` claims the role, ties broken by the lower tab id so two tabs
 * claiming at the same instant settle without a round trip.
 *
 * NO CHANNEL AT ALL (a browser without `BroadcastChannel`, or a test): the tab is its own leader
 * and nothing is relayed. That is the old behaviour, which was correct — just wasteful.
 */

export type LeaderMessage =
  | { kind: "frame"; event: string; data: unknown; id: string | null }
  | { kind: "status"; status: string; lastEventId: string | null }
  /** A tab joined and wants to know where things stand. */
  | { kind: "hello"; tabId: string }
  /** The lease protocol's heartbeat; ignored when Web Locks did the election. */
  | { kind: "lease"; tabId: string; at: number };

export interface LeaderOptions {
  readonly channelName: string;
  readonly lockName: string;
  /** Called when this tab takes the stream over. */
  readonly onLead: () => void;
  /** Called when it stops leading (only the lease path can hand it back). */
  readonly onFollow: () => void;
  readonly onMessage: (message: LeaderMessage) => void;
  /** Seams for the test: no globals are read directly. */
  readonly locks?: LockManagerLike | null;
  readonly channelFactory?: (name: string) => BroadcastChannelLike | null;
  readonly now?: () => number;
}

export interface LockManagerLike {
  request(name: string, options: { mode: "exclusive"; signal?: AbortSignal }, callback: () => Promise<void>): Promise<unknown>;
}

export interface BroadcastChannelLike {
  postMessage(message: unknown): void;
  close(): void;
  onmessage: ((event: { data: unknown }) => void) | null;
}

/** How often a lease leader says it is alive, and how long a follower waits before claiming. */
export const LEASE_INTERVAL_MS = 2_000;
export const LEASE_TIMEOUT_MS = 6_000;

function defaultLocks(): LockManagerLike | null {
  const candidate = (globalThis.navigator as { locks?: LockManagerLike } | undefined)?.locks;
  return candidate && typeof candidate.request === "function" ? candidate : null;
}

function defaultChannel(name: string): BroadcastChannelLike | null {
  const Channel = (globalThis as { BroadcastChannel?: new (name: string) => BroadcastChannelLike }).BroadcastChannel;
  if (!Channel) return null;
  try {
    return new Channel(name);
  } catch {
    return null;
  }
}

export interface LeaderHandle {
  readonly tabId: string;
  isLeader(): boolean;
  /** Leader → followers. A no-op when there is no channel. */
  publish(message: LeaderMessage): void;
  stop(): void;
}

export function createLeader(options: LeaderOptions): LeaderHandle {
  const tabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const now = options.now ?? (() => Date.now());
  const locks = options.locks === undefined ? defaultLocks() : options.locks;
  const channel = (options.channelFactory ?? defaultChannel)(options.channelName);

  let leader = false;
  let stopped = false;
  let release: (() => void) | null = null;
  let leaseTimer: ReturnType<typeof setInterval> | undefined;
  let lastLeaseAt = now();
  let lastLeaseFrom: string | null = null;

  const lead = (): void => {
    if (stopped || leader) return;
    leader = true;
    options.onLead();
  };

  const follow = (): void => {
    if (!leader) return;
    leader = false;
    options.onFollow();
  };

  if (channel) {
    channel.onmessage = (event) => {
      const message = event.data as LeaderMessage | undefined;
      if (!message || typeof message !== "object" || typeof message.kind !== "string") return;
      if (message.kind === "lease") {
        lastLeaseAt = now();
        lastLeaseFrom = message.tabId;
        // Two tabs believing they lead (a laptop resumed from sleep, both leases in flight):
        // the lower id keeps it, so the split settles without either asking the server twice.
        if (leader && !locks && message.tabId < tabId) follow();
        return;
      }
      // A tab just joined: say at once that the role is taken, so it does not claim it after the
      // lease timeout and open a second stream.
      if (message.kind === "hello" && leader && !locks) {
        channel.postMessage({ kind: "lease", tabId, at: now() } satisfies LeaderMessage);
      }
      options.onMessage(message);
    };
  }

  if (locks) {
    // The callback holds the lock: the promise it returns is only resolved by `stop()`, and the
    // browser releases it if this tab dies first.
    void locks
      .request(options.lockName, { mode: "exclusive" }, async () => {
        lead();
        await new Promise<void>((resolve) => {
          release = resolve;
          if (stopped) resolve();
        });
      })
      .catch(() => {
        // No lock manager answer: fall back to leading alone rather than going silent.
        lead();
      });
  } else if (channel) {
    /**
     * Claim only if nobody answers. The first tick is one interval away, and a leader that is
     * already running answers the `hello` below with a lease before then — so a lone tab waits
     * two seconds and a joining tab never opens a second stream.
     */
    lastLeaseAt = now() - LEASE_TIMEOUT_MS + LEASE_INTERVAL_MS;
    channel.postMessage({ kind: "hello", tabId } satisfies LeaderMessage);
    const tick = (): void => {
      if (stopped) return;
      if (leader) {
        channel.postMessage({ kind: "lease", tabId, at: now() } satisfies LeaderMessage);
        return;
      }
      if (now() - lastLeaseAt >= LEASE_TIMEOUT_MS || lastLeaseFrom === tabId) lead();
    };
    leaseTimer = setInterval(tick, LEASE_INTERVAL_MS);
  } else {
    lead();
  }

  return {
    tabId,
    isLeader: () => leader,
    publish(message) {
      if (channel && leader) channel.postMessage(message);
    },
    stop() {
      stopped = true;
      if (leaseTimer) clearInterval(leaseTimer);
      leaseTimer = undefined;
      follow();
      leader = false;
      release?.();
      release = null;
      if (channel) {
        channel.onmessage = null;
        channel.close();
      }
    },
  };
}
